/**
 * SegmentedBarChart — RNW port of the reference
 * `internal/os/apps/web/src/pages/home/charts/SegmentedBarChart.tsx`.
 *
 * Renders a stacked bar chart of Solved / Pending / Urgent counts per bucket.
 * Each bar stacks the three segments bottom-to-top: Solved (green) →
 * Pending (yellow) → Urgent (red), matching the reference color palette
 * and stack order.
 *
 * Key differences from the reference:
 * - No framer-motion; uses React Native Animated for the bar entrance animation.
 * - The tooltip is portalled to <body> (web-only) and positioned with FIXED
 *   viewport coords against the hovered bar's bounding rect. The activity card
 *   floats at the bottom of the map inside a horizontal ScrollView, whose
 *   overflowY is forced to `hidden` by RNW (and CSS won't allow overflow-x:auto
 *   with overflow-y:visible) — so an in-flow absolute tooltip gets clipped by
 *   the strip and can't rise onto the map. The portal escapes that clip entirely.
 * - Data shape uses HomeActivityBucket (urgent/pending/solved) directly.
 * - Text cells use Crop Circle tokens Text — not bare Paper Text.
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Animated, Easing, Platform, StyleSheet, Text, View } from 'react-native';
import { tokens } from '@gripp/shared-ui';
import { HomeActivityBucket } from '../hooks/useHomeActivitySummary';

// Stack order: bottom → top (visual). Matches the reference's resolved/open/urgent order.
const STACK_ORDER = ['solved', 'pending', 'urgent'] as const;
type SegmentKey = (typeof STACK_ORDER)[number];

const SEGMENT_COLORS: Record<SegmentKey, string> = {
  solved: tokens.colors.semantic.success,   // #36B37E — green
  pending: tokens.colors.semantic.warning,  // #F9C431 — yellow
  urgent: tokens.colors.semantic.error,     // #E24D2A — red
};

export interface SegmentedBarChartProps {
  /** Ordered list of activity buckets from the backend. */
  data: HomeActivityBucket[];
  /** Explicit pixel height. Omit to fill the parent (parent must have a defined height). */
  height?: number;
  /** Animation key — changing it re-triggers the entrance animation. */
  animKey?: number | string;
  /** Test ID for the chart container. */
  testID?: string;
}

/**
 * Single animated bar column.
 */
interface BarColumnProps {
  bucket: HomeActivityBucket;
  heightPct: number;
  gap: number;
  isHovered: boolean;
  anyHovered: boolean;
  /** Reports the hovered bar's viewport rect so the portalled tooltip can anchor to it. */
  onHoverIn: (rect: DOMRect) => void;
  onHoverOut: () => void;
  animDelay: number;
}

const BarColumn: React.FC<BarColumnProps> = ({
  bucket,
  heightPct,
  gap,
  isHovered,
  anyHovered,
  onHoverIn,
  onHoverOut,
  animDelay,
}) => {
  const scaleY = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(scaleY, {
      toValue: 1,
      duration: 480,
      delay: animDelay,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = bucket.urgent + bucket.pending + bucket.solved;
  const opacity = isHovered ? 1 : anyHovered ? 0.5 : 0.95;

  return (
    <View
      style={[styles.barWrapper, { marginHorizontal: gap / 2, opacity }]}
      // Hover events only fire on web; safe on native (noop). Reports the bar's
      // viewport rect so the portalled tooltip can anchor above it.
      onPointerEnter={(e: { currentTarget: unknown }) =>
        onHoverIn(
          (e.currentTarget as HTMLElement).getBoundingClientRect()
        )
      }
      onPointerLeave={onHoverOut}
      testID="bar-column"
    >
      <Animated.View
        style={[
          styles.barInner,
          {
            height: `${heightPct}%` as unknown as number,
            transform: [{ scaleY }],
            transformOrigin: 'bottom',
          },
        ]}
      >
        {STACK_ORDER.map((seg) => {
          const v = bucket[seg];
          if (total === 0 || v <= 0) return null;
          return (
            <View
              key={seg}
              style={{
                width: '100%',
                height: `${(v / total) * 100}%` as unknown as number,
                backgroundColor: SEGMENT_COLORS[seg],
              }}
            />
          );
        })}
      </Animated.View>
    </View>
  );
};

export const SegmentedBarChart: React.FC<SegmentedBarChartProps> = ({
  data,
  height,
  animKey,
  testID,
}) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  // Viewport rect of the hovered bar — anchors the portalled (fixed) tooltip.
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null);

  // Recompute when animKey changes so bars re-animate.
  const animVersion = useRef(0);
  const prevAnimKey = useRef(animKey);
  if (animKey !== prevAnimKey.current) {
    animVersion.current += 1;
    prevAnimKey.current = animKey;
  }

  const totals = data.map((b) => b.urgent + b.pending + b.solved);
  const max = Math.max(...totals, 1);
  const gap = data.length > 30 ? 2 : data.length > 14 ? 3 : 4;

  return (
    <View
      style={[styles.container, height !== undefined ? { height } : { flex: 1 }]}
      testID={testID}
    >
      <View style={[styles.barsRow, { gap: 0 }]}>
        {data.map((bucket, i) => (
          <BarColumn
            key={`${bucket.start}-${animVersion.current}`}
            bucket={bucket}
            heightPct={(totals[i] / max) * 100}
            gap={gap}
            isHovered={hoveredIdx === i}
            anyHovered={hoveredIdx !== null}
            onHoverIn={(rect) => {
              setHoveredIdx(i);
              setHoveredRect(rect);
            }}
            onHoverOut={() => {
              setHoveredIdx(null);
              setHoveredRect(null);
            }}
            animDelay={i * 15}
          />
        ))}
      </View>

      {/* Tooltip — web-only, portalled to <body> with fixed viewport coords so it
          escapes the bottom card strip's ScrollView clip (overflowY:hidden) and
          floats freely above on the map. On native it's hidden (not needed). */}
      {Platform.OS === 'web' && hoveredIdx !== null && hoveredRect && data[hoveredIdx] && (
        <BucketTooltip bucket={data[hoveredIdx]} anchorRect={hoveredRect} />
      )}
    </View>
  );
};

SegmentedBarChart.displayName = 'SegmentedBarChart';

// Gap between the hovered bar's top and the tooltip's bottom edge.
const TOOLTIP_GAP = 12;

/**
 * Minimal tooltip that floats above a hovered bar. Portalled to <body> and
 * positioned with FIXED viewport coords (from the bar's bounding rect) so it
 * escapes the bottom card strip's ScrollView clip and renders over the map.
 * `bottom`/`left` are anchored to the bar; `translateY(-100%)` flips the box to
 * grow upward from the gap above the bar.
 */
const BucketTooltip: React.FC<{
  bucket: HomeActivityBucket;
  anchorRect: DOMRect;
}> = ({ bucket, anchorRect }) => {
  const totalCount = bucket.urgent + bucket.pending + bucket.solved;
  const label = new Date(bucket.start).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  // Centre horizontally on the bar; sit a gap above its top edge.
  const left = anchorRect.left + anchorRect.width / 2;
  const top = anchorRect.top - TOOLTIP_GAP;

  const tooltip = (
    <View
      style={[
        styles.tooltip,
        // position:'fixed' is web-only — RNW forwards it; RN types omit it.
        {
          position: 'fixed' as unknown as 'absolute',
          left,
          top,
          transform: [{ translateX: '-50%' as unknown as number }, { translateY: '-100%' as unknown as number }],
        },
      ]}
      pointerEvents="none"
    >
      <Text style={styles.tooltipDate}>{label}</Text>
      <Text style={styles.tooltipTotal}>{totalCount}</Text>
      <TooltipRow color={SEGMENT_COLORS.urgent} label="Urgent" value={bucket.urgent} />
      <TooltipRow color={SEGMENT_COLORS.pending} label="Pending" value={bucket.pending} />
      <TooltipRow color={SEGMENT_COLORS.solved} label="Solved" value={bucket.solved} />
    </View>
  );

  return createPortal(tooltip, document.body);
};

const TooltipRow: React.FC<{ color: string; label: string; value: number }> = ({
  color,
  label,
  value,
}) => (
  <View style={styles.tooltipRow}>
    <View style={[styles.tooltipDot, { backgroundColor: color }]} />
    <Text style={styles.tooltipRowLabel}>{label}</Text>
    <Text style={styles.tooltipRowValue}>{value}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: {
    width: '100%',
    position: 'relative',
  },
  barsRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: '100%',
  },
  barWrapper: {
    flex: 1,
    height: '100%',
    justifyContent: 'flex-end',
  },
  barInner: {
    width: '100%',
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    overflow: 'hidden',
    flexDirection: 'column-reverse',
    justifyContent: 'flex-end',
  },
  tooltip: {
    // position / left / top / transform are set inline (fixed viewport coords).
    backgroundColor: tokens.colors.neutral[700],
    borderRadius: tokens.borderRadius.sm,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    minWidth: 120,
    zIndex: 9999,
    gap: tokens.spacing.xxs,
    ...Platform.select({
      web: {
        boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
      } as object,
    }),
  },
  tooltipDate: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.brand.white,
    marginBottom: tokens.spacing.xxs,
  },
  tooltipTotal: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[400],
  },
  tooltipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  tooltipDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  tooltipRowLabel: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[400],
    flex: 1,
  },
  tooltipRowValue: {
    ...tokens.typography.label.medium,
    fontWeight: '500',
    color: tokens.colors.brand.white,
  },
});
